import React, { useState, useEffect } from 'react'
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  NativeSelect,
} from '@mui/material'

import { useTranslation } from 'react-i18next'
import { useDataApi } from '../../../../context/DataApiContext'
import { useNotificationsProvider } from '../../../../context/NotificationsContext'

// styles
import useStyles from './styles'

import BaseDialog from '../../../BaseDialog'
import DatePicker from '../../../DatePicker'

import enums from '../../../../utils/enums'

export default function ModifySubscriptionDialog({
  title,
  open,
  onClose,
  onModified,
  subscriptionToModify,
}) {
  const classes = useStyles()
  const { t } = useTranslation()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [status, setStatus] = useState(enums.SUBSCRIPTION_STATUS.NOT_SUBSCRIBED)
  const [notes, setNotes] = useState('')
  const [validUntil, setValidUntil] = useState('')

  const resetState = () => {
    // Clean form
    setLoading(false)
    setError(null)
    setStatus(
      subscriptionToModify.status || enums.SUBSCRIPTION_STATUS.NOT_SUBSCRIBED
    )
    setNotes(subscriptionToModify.notes || '')
    // Set default date one month from today
    setValidUntil(
      subscriptionToModify.validUntil
        ? new Date(subscriptionToModify.validUntil)
        : new Date(new Date().setMonth(new Date().getMonth() + 1))
    )
  }

  const onCloseDialog = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose()
    } else {
      resetState()
      onClose()
    }
  }

  const onConfirm = () => {
    if (status === null) {
      setError('status_required')
      return
    }

    if (
      status !== enums.SUBSCRIPTION_STATUS.NOT_SUBSCRIBED &&
      validUntil === null
    ) {
      setError('validUntil_required')
      return
    }

    setLoading(true)

    const data = {
      status,
      notes,
    }

    if (validUntil && status !== enums.SUBSCRIPTION_STATUS.NOT_SUBSCRIBED) {
      data.validUntil = validUntil.toISOString()
    }

    dataProvider
      .put(`users/subscriptions/${subscriptionToModify.id}`, { data })
      .then(() => {
        dataProvider.cache.reset()
        showNotification('success', t('modify-subscription-dialog.success'))
        onModified()
        resetState()
      })
      .catch((err) => {
        setLoading(false)
        if (
          err.response.data.validationErrors &&
          err.response.data.validationErrors.length > 0
        ) {
          showNotification(
            'error',
            err.response.data.validationErrors[0].errorMessage
          )
          setError(`${err.response.data.validationErrors[0].field}_invalid`)
        } else {
          showNotification('error', t('errors.api-error'))
        }
      })
  }

  useEffect(() => {
    if (subscriptionToModify) {
      setStatus(subscriptionToModify.status)
      setNotes(subscriptionToModify.notes || '')
      setValidUntil(
        subscriptionToModify.validUntil
          ? new Date(subscriptionToModify.validUntil)
          : new Date(new Date().setMonth(new Date().getMonth() + 1))
      )
    }
  }, [subscriptionToModify])

  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      onConfirm={onConfirm}
      title={title}
      loading={loading}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <FormControl variant="standard" className={classes.selector}>
            <InputLabel
              id="status-label"
              shrink={enums.SUBSCRIPTION_STATUS.isValid(status)}
            >
              {t('subscriptions.status')}
            </InputLabel>
            <NativeSelect
              labelid="status-label"
              inputProps={{
                name: t('subscriptions.status'),
                id: 'status',
              }}
              onChange={(event) => setStatus(event.target.value)}
              value={status}
            >
              <option value={enums.SUBSCRIPTION_STATUS.NOT_SUBSCRIBED}>
                {t('subscriptions.not-subscribed')}
              </option>
              <option value={enums.SUBSCRIPTION_STATUS.NOT_PAID}>
                {t('subscriptions.not-paid')}
              </option>
              <option value={enums.SUBSCRIPTION_STATUS.PAID}>
                {t('subscriptions.paid')}
              </option>
            </NativeSelect>
          </FormControl>
        </Grid>
        <Grid item>
          <TextField
            variant="standard"
            id="notes"
            label={t('modify-subscription-dialog.notes')}
            error={error === 'notes_required'}
            value={notes}
            type="text"
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            onChange={(e) => setNotes(e.target.value)}
          />
        </Grid>
        {status !== enums.SUBSCRIPTION_STATUS.NOT_SUBSCRIBED && (
          <Grid item>
            <DatePicker
              id="validUntilDate"
              value={validUntil}
              onChange={setValidUntil}
              label={t('modify-subscription-dialog.valid-until')}
            />
          </Grid>
        )}
      </Grid>
    </BaseDialog>
  )
}
